import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import chevronLeft from "@assets/icons/chevron-left.svg";
import chevronRight from "@assets/icons/chevron-right.svg";
import questionTickIcon from "@assets/icons/question-tick.svg";
import styles from "./QuestionsCarousel.module.css";

interface QuestionNumberIndicatorProps {
  label: string;
  isAnswered: boolean;
  isSelected: boolean;
  selectQuestion: () => void;
}

function QuestionNumberIndicator({
  label,
  isAnswered,
  isSelected,
  selectQuestion,
}: QuestionNumberIndicatorProps) {
  return (
    <div
      className={`${styles.questionIndicator} ${
        isAnswered && isSelected
          ? styles.answeredAndSelected
          : isAnswered
          ? styles.answered
          : isSelected
          ? styles.selected
          : ""
      }`}
      onClick={selectQuestion}
    >
      {isAnswered && <img src={questionTickIcon} alt="Answered" />}
      <span className={styles.questionNumber}>{label}</span>
    </div>
  );
}

interface QuestionsCarouselProps {
  questions: any[];
  questionIndex: number;
  onQuestionSelect: (index: number) => void;
  showReportSummaryNav?: boolean;
  reportSummaryIndex?: number;
}

function QuestionsCarousel({
  questions,
  questionIndex,
  onQuestionSelect,
  showReportSummaryNav = false,
  reportSummaryIndex = -1,
}: QuestionsCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    dragFree: true,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollButtons();
    emblaApi.on("select", updateScrollButtons);
    emblaApi.on("reInit", updateScrollButtons);

    return () => {
      emblaApi.off("select", updateScrollButtons);
      emblaApi.off("reInit", updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi]
  );

  const toCarouselIndex = useCallback(
    (index: number) => {
      if (!showReportSummaryNav) return index;
      return index === reportSummaryIndex ? 0 : index + 1;
    },
    [showReportSummaryNav, reportSummaryIndex]
  );

  // Sync external questionIndex changes with carousel
  useEffect(() => {
    if (emblaApi && questionIndex !== undefined) {
      emblaApi.scrollTo(toCarouselIndex(questionIndex));
    }
  }, [emblaApi, questionIndex, toCarouselIndex]);

  return (
    <div className={styles.questionsNavigationWrapper}>
      {canScrollPrev && (
        <div className={styles.chevronIcons} onClick={scrollPrev}>
          <img src={chevronLeft} alt="Previous" />
        </div>
      )}

      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {showReportSummaryNav && (
            <div className={styles.emblaSlide}>
              <QuestionNumberIndicator
                label="ALL"
                isAnswered={false}
                isSelected={questionIndex === reportSummaryIndex}
                selectQuestion={() => {
                  onQuestionSelect(reportSummaryIndex);
                  scrollTo(0);
                }}
              />
            </div>
          )}
          {questions.map((question, index) => (
            <div key={index} className={styles.emblaSlide}>
              <QuestionNumberIndicator
                label={String(index + 1)}
                isAnswered={!!question.current_evaluation}
                isSelected={index === questionIndex}
                selectQuestion={() => {
                  onQuestionSelect(index);
                  scrollTo(toCarouselIndex(index));
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {canScrollNext && (
        <div className={styles.chevronIcons} onClick={scrollNext}>
          <img src={chevronRight} alt="Next" />
        </div>
      )}
    </div>
  );
}

export default QuestionsCarousel;
